import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrencyInput } from '../../hooks/useCurrencyInput';
import {
  ArrowLeft,
  Briefcase,
  Calendar,
  CheckCircle2,
  FileText,
  MapPin,
  Send,
  Trash2,
  UploadCloud,
  Wallet,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  manproQueryKeys,
  mapProviderOpportunityToCard,
  providerBidService,
  providerOpportunityService,
  type BidDocumentCategory,
} from '../../api/manpro';
import { getOpportunityAction } from '../../lib/manproOpportunityMedia';

const MAX_DOCUMENT_SIZE = 20 * 1024 * 1024;

const documentCategoryOptions: Array<{ value: BidDocumentCategory; label: string }> = [
  { value: 'portfolio', label: 'Portfolio' },
  { value: 'company_profile', label: 'Company Profile' },
  { value: 'rab', label: 'RAB / Breakdown Harga' },
  { value: 'method', label: 'Metode Kerja' },
  { value: 'legal', label: 'Legalitas / Sertifikat' },
  { value: 'other', label: 'Lainnya' },
];

type BidFormState = {
  duration: string;
  note: string;
  methodStatement: string;
  commercialTerms: string;
  documentCategory: BidDocumentCategory;
};

type LocalBidDocument = {
  id: string;
  file: File;
  category: BidDocumentCategory;
};

const formatFileSize = (size: number) => {
  if (!size) return 'Ukuran tidak tersedia';

  const units = ['B', 'KB', 'MB', 'GB'];
  const unitIndex = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
  const value = size / 1024 ** unitIndex;

  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const getErrorMessage = (error: unknown) => {
  const maybeError = error as {
    response?: {
      data?: {
        message?: string;
        errors?: Array<{ message?: string; code?: string; error_code?: string }>;
      };
    };
  };

  const firstValidationMessage = maybeError.response?.data?.errors?.[0]?.message;
  const backendMessage = maybeError.response?.data?.message;

  if (firstValidationMessage?.toLowerCase().includes('already submitted')) {
    return 'Anda sudah pernah mengirim bid untuk proyek ini. Silakan cek halaman Penawaran Saya.';
  }

  return firstValidationMessage || backendMessage || 'Bid gagal dikirim. Periksa kembali data penawaran.';
};

const ProviderBidCreate = () => {
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [bidForm, setBidForm] = useState<BidFormState>({
    duration: '',
    note: '',
    methodStatement: '',
    commercialTerms: '',
    documentCategory: 'portfolio',
  });
  const amountInput = useCurrencyInput(0);
  const [bidDocuments, setBidDocuments] = useState<LocalBidDocument[]>([]);

  const opportunityQuery = useQuery({
    queryKey: manproQueryKeys.providerOpportunity(opportunityId || ''),
    // Form bid harus fetch opportunity dari API supaya route hasil list dinamis tidak jatuh ke data prototype.
    queryFn: () => providerOpportunityService.getProviderOpportunity(opportunityId || ''),
    enabled: Boolean(opportunityId),
  });

  const createBidMutation = useMutation({
    mutationFn: () =>
      providerBidService.createProviderBid(opportunityId || '', {
        amount: String(amountInput.value),
        duration: bidForm.duration,
        note: bidForm.note,
        method_statement: bidForm.methodStatement,
        commercial_terms: bidForm.commercialTerms,
        documents: bidDocuments.map((document) => document.file),
        document_categories: bidDocuments.map((document) => document.category),
      }),
    onSuccess: () => {
      toast.success('Bid provider berhasil dikirim ke Owner.');
      queryClient.invalidateQueries({ queryKey: ['manpro', 'provider', 'opportunities'] });
      queryClient.invalidateQueries({ queryKey: manproQueryKeys.providerOpportunity(opportunityId || '') });
      queryClient.invalidateQueries({ queryKey: ['manpro', 'provider', 'bids'] });
      navigate('/provider-bids');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const view = opportunityQuery.data ? mapProviderOpportunityToCard(opportunityQuery.data) : null;
  const primaryAction = getOpportunityAction(view, `/provider-opportunities/${view?.id}/bid`);

  const handleDocumentUpload = (files: FileList | null) => {
    if (!files?.length) return;

    const filesArray = Array.from(files);
    const oversizedFile = filesArray.find((file) => file.size > MAX_DOCUMENT_SIZE);

    if (oversizedFile) {
      toast.error(`${oversizedFile.name} melebihi batas 20MB.`);
      return;
    }

    const nextDocuments = filesArray.map((file, index) => ({
      id: `provider-bid-doc-${Date.now()}-${index}`,
      file,
      category: bidForm.documentCategory,
    }));

    setBidDocuments((previous) => [...previous, ...nextDocuments]);
    toast.success(`${nextDocuments.length} dokumen penawaran ditambahkan.`);
  };

  const removeDocument = (documentId: string) => {
    setBidDocuments((previous) => previous.filter((document) => document.id !== documentId));
    toast.success('Dokumen penawaran dihapus.');
  };

  const submitBid = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const amount = amountInput.value;

    if (!amount || amount <= 0) {
      toast.error('Nominal bid harus diisi.');
      return;
    }

    if (!bidForm.duration.trim()) {
      toast.error('Durasi estimasi wajib diisi.');
      return;
    }

    if (view?.hasBid) {
      toast.error('Anda sudah pernah mengirim bid untuk proyek ini.');
      return;
    }

    if (view?.isDeadlinePassed) {
      toast.error('Deadline bid sudah lewat.');
      return;
    }

    createBidMutation.mutate();
  };

  if (opportunityQuery.isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-6 w-52 animate-pulse rounded bg-slate-100" />
        <section className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="h-[620px] animate-pulse rounded-2xl bg-white" />
          <aside className="h-80 animate-pulse rounded-2xl bg-white" />
        </section>
      </div>
    );
  }

  if (opportunityQuery.isError || !opportunityQuery.data || !view) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
        <FileText size={40} className="mx-auto text-slate-300" />
        <h1 className="mt-4 text-xl font-extrabold text-slate-900">Opportunity tidak ditemukan</h1>
        <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-slate-500">
          Data opportunity tidak dapat dimuat dari endpoint Provider MANPRO.
        </p>
        <Link to="/provider-opportunities" className="mt-5 inline-flex rounded-xl bg-blue-600 px-5 py-3 text-sm font-extrabold text-white">
          Kembali ke Peluang Provider
        </Link>
      </div>
    );
  }

  if (view.isRedirectOpportunity) {
    return (
      <div className="rounded-2xl border border-blue-100 bg-white p-10 text-center shadow-sm">
        <h1 className="text-2xl font-extrabold text-slate-900">Opportunity eksternal</h1>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500">
          Proyek ini berasal dari pengadaan resmi LPSE. Bid tidak dikirim melalui Indoteknisi, melainkan dibuka di situs resmi pengadaan.
        </p>
        <a
          href={view.sourceUrl || '#'}
          target="_blank"
          rel="noreferrer"
          className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-extrabold text-white transition hover:bg-blue-700"
        >
          <Send size={16} />
          {primaryAction.label}
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link to={`/provider-opportunities/${view.id}`} className="inline-flex items-center gap-2 text-sm font-extrabold text-blue-700">
        <ArrowLeft size={16} />
        Kembali ke Detail Opportunity
      </Link>

      <section>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Kirim Bid Provider</h1>
        <p className="mt-2 text-sm text-slate-500">
          Susun penawaran berdasarkan data yang dimasukkan Owner saat membuat proyek.
        </p>
      </section>

      <section className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <form onSubmit={submitBid} className="min-w-0 space-y-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          {view.hasBid && (
            <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4 text-sm font-semibold leading-6 text-amber-800">
              Anda sudah pernah mengirim bid untuk proyek ini. Silakan cek statusnya di halaman Penawaran Saya.
            </div>
          )}

          {view.isDeadlinePassed && (
            <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4 text-sm font-semibold leading-6 text-rose-800">
              Deadline bid sudah lewat, penawaran baru tidak dapat dikirim.
            </div>
          )}

          <label className="block space-y-2">
            <span className="text-sm font-bold text-slate-700">Nilai Bid</span>
            <input
              type="text"
              value={amountInput.displayValue}
              onChange={amountInput.handleChange}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
              placeholder="Contoh: Rp 250.000.000"
            />
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-bold text-slate-700">Durasi Estimasi</span>
            <input
              value={bidForm.duration}
              onChange={(event) => setBidForm((prev) => ({ ...prev, duration: event.target.value }))}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
              placeholder="Contoh: 45 hari"
            />
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-bold text-slate-700">Catatan Penawaran</span>
            <textarea
              value={bidForm.note}
              onChange={(event) => setBidForm((prev) => ({ ...prev, note: event.target.value }))}
              rows={5}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
              placeholder="Jelaskan cakupan pekerjaan, asumsi harga, material, termin, garansi, dan catatan komersial."
            />
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-bold text-slate-700">Metode Kerja Singkat</span>
            <textarea
              value={bidForm.methodStatement}
              onChange={(event) => setBidForm((prev) => ({ ...prev, methodStatement: event.target.value }))}
              rows={5}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
              placeholder="Contoh: survey lokasi, finalisasi scope, pengadaan material, eksekusi, QC, serah terima."
            />
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-bold text-slate-700">Syarat Komersial</span>
            <textarea
              value={bidForm.commercialTerms}
              onChange={(event) => setBidForm((prev) => ({ ...prev, commercialTerms: event.target.value }))}
              rows={4}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
              placeholder="Contoh: termin pembayaran, masa berlaku penawaran, exclusion, garansi."
            />
          </label>

          <div className="space-y-3">
            <div>
              <h2 className="text-sm font-bold text-slate-700">Dokumen Pendukung Provider</h2>
              <p className="mt-1 text-xs text-slate-500">
                Tambahkan portfolio, company profile, RAB, metode kerja, atau legalitas untuk memperkuat bid.
              </p>
            </div>

            <label className="block space-y-2">
              <span className="text-sm font-bold text-slate-700">Kategori Dokumen</span>
              <select
                value={bidForm.documentCategory}
                onChange={(event) =>
                  setBidForm((prev) => ({
                    ...prev,
                    documentCategory: event.target.value as BidDocumentCategory,
                  }))
                }
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
              >
                {documentCategoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-blue-200 bg-blue-50/50 px-4 py-6 text-center transition hover:border-blue-300 hover:bg-blue-50">
              <UploadCloud size={28} className="text-blue-600" />
              <span className="mt-2 text-sm font-bold text-slate-700">Upload dokumen penawaran</span>
              <span className="mt-1 text-xs text-slate-500">
                PDF, DOC, XLS, JPG, PNG. Maksimal 20MB per file.
              </span>
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                onChange={(event) => {
                  handleDocumentUpload(event.target.files);
                  event.target.value = '';
                }}
                className="hidden"
              />
            </label>

            {bidDocuments.length > 0 && (
              <div className="space-y-2 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                {bidDocuments.map((document) => {
                  const categoryLabel =
                    documentCategoryOptions.find((option) => option.value === document.category)?.label || 'Dokumen';

                  return (
                    <div key={document.id} className="flex items-center justify-between gap-3 rounded-xl bg-white px-3 py-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-extrabold text-slate-800">{document.file.name}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          {categoryLabel} • {formatFileSize(document.file.size)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeDocument(document.id)}
                        className="rounded-lg p-2 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                        aria-label={`Hapus ${document.file.name}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <button
            disabled={createBidMutation.isPending || view.hasBid || view.isDeadlinePassed}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            <Send size={16} />
            {createBidMutation.isPending ? 'Mengirim Bid...' : 'Kirim Bid ke Owner'}
          </button>
        </form>

        <aside className="w-full space-y-4 lg:sticky lg:top-24 lg:w-[360px]">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="font-extrabold text-slate-900">{view.title}</h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">{view.description}</p>

            <div className="mt-5 space-y-3 text-sm font-semibold text-slate-600">
              <p className="flex items-center gap-2">
                <Wallet size={16} className="text-slate-400" />
                Budget Owner: {view.budgetLabel}
              </p>
              <p className="flex items-center gap-2">
                <MapPin size={16} className="text-slate-400" />
                {view.location}
              </p>
              <p className="flex items-center gap-2">
                <Briefcase size={16} className="text-slate-400" />
                {view.procurementType}
              </p>
              <p className="flex items-center gap-2">
                <Calendar size={16} className="text-slate-400" />
                Deadline: {view.deadlineLabel}
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5">
            <h2 className="font-extrabold text-slate-900">Checklist Bid</h2>
            <div className="mt-4 space-y-3 text-sm font-semibold text-slate-600">
              {[
                'Harga sesuai scope Owner',
                'Durasi realistis',
                'Metode kerja jelas',
                'Catatan asumsi dan exclusion ditulis',
                'Dokumen Owner sudah direview',
              ].map((item) => (
                <p key={item} className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  {item}
                </p>
              ))}
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
};

export default ProviderBidCreate;
